import Phaser from 'phaser';
import { FONT_FAMILY } from '../config/constants';
import { crispText } from './textUtils';

export interface ButtonConfig {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  color: number;
  hoverColor: number;
  onClick: () => void;
  /** Optional container to add button elements to */
  container?: Phaser.GameObjects.Container;
  /** Font size string (default '22px') */
  fontSize?: string;
  /** Corner radius (default 10) */
  radius?: number;
  /** Shadow offset for the drop shadow (default 2). Set to 0 to disable shadow. */
  shadowOffset?: number;
  /** Shadow alpha (default 0.25) */
  shadowAlpha?: number;
  /** Gloss highlight inset from edges (default 3) */
  glossInset?: number;
  /** Gloss highlight alpha (default 0.1) */
  glossAlpha?: number;
  /** If true, stop propagation on pointerdown (default false) */
  stopPropagation?: boolean;
  /** Text color for the label (default '#ffffff') */
  textColor?: string;
  /** Stroke color for border (optional — if set, draws a stroke instead of gloss/shadow style) */
  strokeColor?: number;
  /** Fill alpha (default 1) */
  fillAlpha?: number;
}

/**
 * Create a reusable styled button with hover/press states.
 * Returns the hit-area rectangle for further customization if needed.
 */
export function createButton(scene: Phaser.Scene, config: ButtonConfig): Phaser.GameObjects.Rectangle {
  const {
    x, y, w, h, label, color, hoverColor, onClick,
    container,
    fontSize = '44px',
    radius: r = 20,
    shadowOffset = 4,
    shadowAlpha = 0.25,
    glossInset = 6,
    glossAlpha = 0.1,
    stopPropagation = false,
    textColor = '#ffffff',
    strokeColor,
    fillAlpha = 1,
  } = config;

  const gfx = scene.add.graphics();

  // Two drawing modes: stroke-style (PauseOverlay) or shadow+gloss-style (Menu/GameOver)
  const isStrokeStyle = strokeColor !== undefined;

  const draw = (c: number, pressed = false) => {
    gfx.clear();
    if (isStrokeStyle) {
      gfx.fillStyle(c, fillAlpha);
      gfx.fillRoundedRect(x - w / 2, y - h / 2, w, h, r);
      gfx.lineStyle(3, strokeColor!, 0.7);
      gfx.strokeRoundedRect(x - w / 2, y - h / 2, w, h, r);
    } else {
      if (!pressed && shadowOffset > 0) {
        gfx.fillStyle(0x000000, shadowAlpha);
        gfx.fillRoundedRect(x - w / 2 + 4, y - h / 2 + shadowOffset + 2, w, h, r);
      }
      gfx.fillStyle(c, 1);
      gfx.fillRoundedRect(x - w / 2, y - h / 2 + (pressed ? 4 : 0), w, h, r);
      if (glossAlpha > 0) {
        gfx.fillStyle(0xffffff, glossAlpha);
        gfx.fillRoundedRect(
          x - w / 2 + glossInset,
          y - h / 2 + (pressed ? 6 : 2),
          w - glossInset * 2,
          h * 0.35,
          { tl: r - 4, tr: r - 4, bl: 0, br: 0 },
        );
      }
    }
  };

  draw(color);

  const addTo = (obj: Phaser.GameObjects.GameObject) => {
    if (container) container.add(obj);
  };

  addTo(gfx);

  const hit = scene.add.rectangle(x, y, w, h, 0x000000, 0);
  hit.setInteractive({ useHandCursor: true });
  addTo(hit);

  const text = crispText(scene.add.text(x, y, label, {
    fontFamily: FONT_FAMILY,
    fontSize,
    color: textColor,
    fontStyle: 'bold',
  })).setOrigin(0.5);
  addTo(text);

  if (isStrokeStyle) {
    hit.on('pointerover', () => text.setColor('#ffffff'));
    hit.on('pointerout', () => text.setColor(textColor));
    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (stopPropagation) pointer.event.stopPropagation();
      onClick();
    });
  } else {
    hit.on('pointerover', () => draw(hoverColor));
    hit.on('pointerout', () => draw(color));
    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (stopPropagation) pointer.event.stopPropagation();
      draw(hoverColor, true);
      text.setY(y + 4);
    });
    hit.on('pointerup', () => {
      draw(color);
      text.setY(y);
      onClick();
    });
  }

  return hit;
}
